import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { message } from 'antd';
import './styles/filesModule.css';

import {
    fetchFilesTree,
    fetchFiles,
    uploadProjectFile,
    getFileDownloadUrl,
    getFilePreviewUrl,
    archiveProjectFile,
    restoreProjectFile,
    publishDeliverableToCRE,
} from './api/projectFilesApi';

import FilesSidebarTree from './components/FilesSidebarTree';
import FilesExplorerHeader from './components/FilesExplorerHeader';
import FilesGridView from './components/FilesGridView';
import FilesListView from './components/FilesListView';
import FilePreviewSection from './components/FilePreviewSection';
import FileRevisionHistory from './components/FileRevisionHistory';
import FileSearchModal from './components/FileSearchModal';
import FileUploadModal from './components/FileUploadModal';
import FilePreviewModal from './components/FilePreviewModal';

/**
 * ProjectFiles Module Component
 * 100% Live Database Implementation for ERP Project Management Files Module
 * Powered strictly by MongoDB StorageFile records, Project models, and Cloudflare R2 storage.
 */
export default function ProjectFiles({ onNavigate = null, scopedProject = null }) {
    const scopedProjectId = scopedProject?._id ? String(scopedProject._id) : scopedProject?.id ? String(scopedProject.id) : null;
    // ── Hierarchy & Tree State (100% Real from Database) ──
    const [activeSection, setActiveSection] = useState('client'); // 'client' | 'internal'
    const [clientProjects, setClientProjects] = useState([]);
    const [archivedProjects, setArchivedProjects] = useState([]);
    const [internalWork, setInternalWork] = useState([]);

    const defaultScopedProject = useMemo(() => {
        if (!scopedProjectId) return null;
        return {
            id: scopedProjectId,
            name: scopedProject?.projectName || scopedProject?.name || 'Project',
            code: scopedProject?.projectNumber || scopedProject?.code || '',
            status: scopedProject?.status || 'In Progress',
            totalFiles: 0,
            subfolders: [
                { id: `${scopedProjectId}_01`, code: '01', name: '01 - Requirements', count: 0 },
                { id: `${scopedProjectId}_02`, code: '02', name: '02 - Resources', count: 0 },
                { id: `${scopedProjectId}_03`, code: '03', name: '03 - Revisions', count: 0 },
                { id: `${scopedProjectId}_04`, code: '04', name: '04 - Meeting Notes', count: 0 },
                { id: `${scopedProjectId}_05`, code: '05', name: '05 - Deliverables', count: 0 },
            ],
        };
    }, [scopedProjectId, scopedProject]);

    const [selectedProject, setSelectedProject] = useState(() => defaultScopedProject);
    const [selectedFolder, setSelectedFolder] = useState(() => defaultScopedProject?.subfolders?.[0] || null);

    // ── Files & Explorer State (100% Real from Database) ──
    const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'
    const [fileStatus, setFileStatus] = useState('active'); // 'active' | 'archived'
    const [filesList, setFilesList] = useState([]);
    const [selectedFile, setSelectedFile] = useState(null);
    const [loadingFiles, setLoadingFiles] = useState(false);
    const [treeLoading, setTreeLoading] = useState(true);

    // ── Modals & Preview State ──
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [isUploadOpen, setIsUploadOpen] = useState(false);
    const [previewModal, setPreviewModal] = useState({ isOpen: false, file: null, previewUrl: null });

    // Client-side cache for instant (0ms) folder navigation
    const filesCacheRef = useRef(new Map());

    // Memory retention for instantaneous tab switching without resetting or reloading
    const lastClientSelectionRef = useRef({ project: null, folder: null });
    const lastInternalSelectionRef = useRef({ project: null, folder: null });
    const [isRefreshing, setIsRefreshing] = useState(false);

    // Detect if the selected project is an archived project
    const isArchivedProject = useMemo(() => {
        if (!selectedProject) return false;
        return ['Archived', 'Cancelled', 'Canceled'].includes(selectedProject.status) || archivedProjects.some((p) => p.id === selectedProject.id);
    }, [selectedProject, archivedProjects]);

    // ── 1. Fetch Real Projects & Standard Subfolders Tree from MongoDB ──
    const loadTree = useCallback(async (isManual = false) => {
        if (isManual) {
            setIsRefreshing(true);
        } else {
            setTreeLoading(true);
        }
        try {
            const res = await fetchFilesTree();
            if (res?.success && res?.data) {
                const { clientWork, internalWork: iWork } = res.data;
                const active = clientWork?.active || [];
                const archived = clientWork?.archived || [];
                const internal = iWork || [];

                setClientProjects(active);
                setArchivedProjects(archived);
                setInternalWork(internal);

                if (scopedProjectId) {
                    const inHouseList = internal.flatMap((b) => (b.folders || []).flatMap((f) => f.projects || []));
                    const allTreeProjects = [...active, ...archived, ...inHouseList];
                    let matchedProj = allTreeProjects.find((p) => String(p.id) === scopedProjectId);
                    if (!matchedProj) {
                        matchedProj = {
                            id: scopedProjectId,
                            name: scopedProject?.projectName || scopedProject?.name || 'Project',
                            code: scopedProject?.projectNumber || scopedProject?.code || '',
                            status: scopedProject?.status || 'In Progress',
                            totalFiles: 0,
                            subfolders: [
                                { id: `${scopedProjectId}_01`, code: '01', name: '01 - Requirements', count: 0 },
                                { id: `${scopedProjectId}_02`, code: '02', name: '02 - Resources', count: 0 },
                                { id: `${scopedProjectId}_03`, code: '03', name: '03 - Revisions', count: 0 },
                                { id: `${scopedProjectId}_04`, code: '04', name: '04 - Meeting Notes', count: 0 },
                                { id: `${scopedProjectId}_05`, code: '05', name: '05 - Deliverables', count: 0 },
                            ],
                        };
                    }
                    setSelectedProject(matchedProj);
                    setSelectedFolder((prev) => {
                        if (prev && String(prev.id).startsWith(scopedProjectId)) {
                            if (prev.code === 'all') return { id: `${scopedProjectId}_all`, code: 'all', name: 'All Project Files' };
                            const updatedFolder = (matchedProj.subfolders || []).find((sf) => sf.id === prev.id || sf.code === prev.code);
                            if (updatedFolder) return updatedFolder;
                            for (const sf of matchedProj.subfolders || []) {
                                const child = (sf.children || []).find((c) => c.id === prev.id);
                                if (child) return child;
                            }
                        }
                        return matchedProj.subfolders?.[0] || { id: `${scopedProjectId}_all`, code: 'all', name: 'All Project Files' };
                    });
                    return;
                }

                // Initialize default references if empty
                if (!lastClientSelectionRef.current.project && active.length > 0) {
                    lastClientSelectionRef.current = { project: active[0], folder: active[0].subfolders?.[0] };
                }
                if (!lastInternalSelectionRef.current.project && internal.length > 0) {
                    lastInternalSelectionRef.current = { project: internal[0], folder: { id: `${internal[0].id}_all`, code: 'all', name: 'All Files' } };
                }

                // Set initial active project & subfolder from real data only on first load
                setSelectedProject((prev) => {
                    if (prev) return prev;
                    return activeSection === 'internal' ? lastInternalSelectionRef.current.project : lastClientSelectionRef.current.project;
                });
                setSelectedFolder((prev) => {
                    if (prev) return prev;
                    return activeSection === 'internal' ? lastInternalSelectionRef.current.folder : lastClientSelectionRef.current.folder;
                });
            }
        } catch (err) {
            console.error('[ProjectFiles] Error loading real files tree:', err);
            message.error('Unable to load project folder tree from database.');
        } finally {
            setTreeLoading(false);
            setIsRefreshing(false);
        }
    }, [activeSection]);

    useEffect(() => {
        loadTree();
    }, [scopedProjectId]); // Only fetch tree on initial mount or scopedProjectId change

    // ── 2. Fetch Real Files for the Active Project & Folder with Instant Cache + Background Revalidation ──
    useEffect(() => {
        if (!selectedProject?.id) {
            setFilesList([]);
            setSelectedFile(null);
            return;
        }

        let isMounted = true;
        const loadFiles = async () => {
            const isInternal = selectedProject.id?.startsWith('brand-');
            let folderCode;
            if (isInternal) {
                folderCode = selectedFolder?.code || (selectedFolder?.id?.replace(/^dymax-|^e360-|^cport-/, '') || 'all');
            } else if (selectedFolder?.code === 'all' || selectedFolder?.id?.endsWith('_all')) {
                folderCode = 'all';
            } else {
                folderCode = selectedFolder?.code || (selectedFolder?.id?.includes('_') ? selectedFolder.id.split('_')[1] : '01');
            }

            const effectiveStatus = isArchivedProject ? 'archived' : fileStatus;
            const cacheKey = `${selectedProject.id}_${folderCode}_${effectiveStatus}_${selectedFolder?.subcat || ''}_${selectedFolder?.revisionRound || ''}`;

            // Check client memory cache for 0ms instant display
            if (filesCacheRef.current.has(cacheKey)) {
                const cachedData = filesCacheRef.current.get(cacheKey);
                setFilesList(cachedData);
                setSelectedFile((prev) => (prev && cachedData.some((f) => f.id === prev.id) ? prev : (cachedData[0] || null)));
                setLoadingFiles(false);
            } else {
                setLoadingFiles(true);
            }

            try {
                const res = await fetchFiles({
                    projectId: selectedProject.id,
                    folderCode,
                    status: effectiveStatus,
                    subcat: selectedFolder?.subcat,
                    revisionRound: selectedFolder?.revisionRound,
                });

                if (isMounted) {
                    if (res?.success && Array.isArray(res.data)) {
                        filesCacheRef.current.set(cacheKey, res.data);
                        setFilesList(res.data);
                        setSelectedFile((prev) => (prev && res.data.some((f) => f.id === prev.id) ? prev : (res.data[0] || null)));
                    } else if (!filesCacheRef.current.has(cacheKey)) {
                        setFilesList([]);
                        setSelectedFile(null);
                    }
                }
            } catch (err) {
                console.error('[ProjectFiles] Error fetching real files:', err);
                if (isMounted && !filesCacheRef.current.has(cacheKey)) {
                    setFilesList([]);
                    setSelectedFile(null);
                }
            } finally {
                if (isMounted) setLoadingFiles(false);
            }
        };

        loadFiles();
        return () => {
            isMounted = false;
        };
    }, [selectedProject?.id, selectedFolder?.id, selectedFolder?.code, selectedFolder?.subcat, selectedFolder?.revisionRound, fileStatus, isArchivedProject]);

    // ── Handlers ──
    const handleSelectFolder = (project, folder) => {
        setSelectedProject(project);
        setSelectedFolder(folder);
        if (activeSection === 'internal') {
            lastInternalSelectionRef.current = { project, folder };
        } else {
            lastClientSelectionRef.current = { project, folder };
        }
        // If clicking an archived project, automatically switch to archived view
        if (['Archived', 'Cancelled', 'Canceled'].includes(project?.status) || archivedProjects.some((p) => p.id === project?.id)) {
            setFileStatus('archived');
        } else {
            setFileStatus('active');
        }
    };

    const handleSelectFile = (file) => {
        setSelectedFile(file);
    };

    // Real Download Handler (calls Cloudflare R2 presigned URL endpoint)
    const handleDownload = async (file) => {
        if (!file?.id) return;
        message.loading({ content: `Generating secure Cloudflare R2 download for ${file.name}...`, key: 'dl' });
        try {
            const res = await getFileDownloadUrl(file.id);
            if (res?.downloadUrl) {
                window.open(res.downloadUrl, '_blank');
                message.success({ content: `${file.name} download started.`, key: 'dl' });
            } else {
                message.error({ content: 'Could not generate download URL.', key: 'dl' });
            }
        } catch (err) {
            console.error('[ProjectFiles] Download error:', err);
            const errMsg = err?.response?.data?.message || 'Server error retrieving file download.';
            message.error({ content: errMsg, key: 'dl' });
        }
    };

    // Real Inline Preview Handler (calls Cloudflare R2 presigned preview URL endpoint)
    const handlePreviewFullScreen = async (file) => {
        if (!file) return;
        let previewUrl = null;
        try {
            if (file.id) {
                const res = await getFilePreviewUrl(file.id);
                if (res?.previewUrl) {
                    previewUrl = res.previewUrl;
                }
            }
        } catch (err) {
            console.warn('[ProjectFiles] Could not retrieve presigned preview URL:', err);
        }

        setPreviewModal({
            isOpen: true,
            file,
            previewUrl,
        });
    };

    // Real Upload Handler (streams binary buffer to Cloudflare R2 via backend)
    const handleUploadSubmit = async (formData, fallbackMeta) => {
        message.loading({ content: `Streaming "${fallbackMeta.fileName}" directly to Cloudflare R2...`, key: 'up' });
        try {
            const res = await uploadProjectFile(formData);
            if (res?.success && res?.data) {
                filesCacheRef.current.clear();
                setFilesList((prev) => [res.data, ...prev]);
                setSelectedFile(res.data);
                message.success({ content: `"${res.data.name}" successfully uploaded to Cloudflare R2 & recorded in database!`, key: 'up' });

                // Refresh tree file counts
                loadTree();
            } else {
                message.error({ content: res?.message || 'Failed to upload file.', key: 'up' });
            }
        } catch (err) {
            console.error('[ProjectFiles] Upload error:', err);
            message.error({ content: err?.response?.data?.message || 'Upload to Cloudflare R2 failed.', key: 'up' });
        }
    };

    // Real Soft-Archive Handler (removes from active view, preserves in database & R2)
    const handleArchiveFile = async (file) => {
        if (!file?.id) return;
        message.loading({ content: `Archiving ${file.name}...`, key: 'arch' });
        try {
            await archiveProjectFile(file.id);
            filesCacheRef.current.clear();
            setFilesList((prev) => prev.filter((f) => f.id !== file.id));
            setSelectedFile((prev) => (prev?.id === file.id ? null : prev));
            setPreviewModal({ isOpen: false, file: null, previewUrl: null });
            message.success({ content: `File "${file.name}" archived. It remains safely in R2 and can be restored at any time.`, key: 'arch' });
            loadTree();
        } catch (err) {
            console.error('[ProjectFiles] Archive error:', err);
            message.error({ content: err?.response?.data?.message || 'Failed to archive file.', key: 'arch' });
        }
    };

    // Real Restore Handler (restores file back to active folder)
    const handleRestoreFile = async (file) => {
        if (!file?.id) return;
        message.loading({ content: `Restoring ${file.name}...`, key: 'rest' });
        try {
            await restoreProjectFile(file.id);
            filesCacheRef.current.clear();
            setFilesList((prev) => prev.filter((f) => f.id !== file.id));
            setSelectedFile((prev) => (prev?.id === file.id ? null : prev));
            setPreviewModal({ isOpen: false, file: null, previewUrl: null });
            message.success({ content: `File "${file.name}" restored to active folder.`, key: 'rest' });
            loadTree();
        } catch (err) {
            console.error('[ProjectFiles] Restore error:', err);
            message.error({ content: err?.response?.data?.message || 'Failed to restore file.', key: 'rest' });
        }
    };

    // Real Publish Deliverable to CRE Handler
    const handlePublishToCRE = async (file) => {
        if (!file?.id) return;
        message.loading({ content: `Publishing deliverable "${file.name}" to Client Relationship Engine (CRE)...`, key: 'cre' });
        try {
            const res = await publishDeliverableToCRE(file.id);
            if (res?.success) {
                const creRef = res.data?.creFileId || 'CRE-SYNCED';
                message.success({
                    content: `"${file.name}" successfully published to CRE! (Ref: ${creRef})`,
                    key: 'cre',
                    duration: 4,
                });
                // Update local state with CRE metadata
                const updateFileState = (item) => ({
                    ...item,
                    publishedToCRE: true,
                    creFileId: creRef,
                    syncStatus: 'synced',
                    publishedAt: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
                });

                setFilesList((prev) =>
                    prev.map((f) => (f.id === file.id ? updateFileState(f) : f))
                );
                setSelectedFile((prev) =>
                    prev?.id === file.id ? updateFileState(prev) : prev
                );
            } else {
                message.error({ content: res?.message || 'Could not publish deliverable to CRE.', key: 'cre' });
            }
        } catch (err) {
            console.error('[ProjectFiles] Publish to CRE error:', err);
            message.error({ content: err?.response?.data?.message || 'Failed to publish to CRE.', key: 'cre' });
        }
    };

    // Manual Refresh handler to sync new files without reloading whole page
    const handleManualRefresh = async () => {
        setIsRefreshing(true);
        filesCacheRef.current.clear();
        await loadTree(true);
        if (selectedProject?.id) {
            setLoadingFiles(true);
            try {
                const isInternal = selectedProject.id?.startsWith('brand-');
                let folderCode;
                if (isInternal) {
                    folderCode = selectedFolder?.code || (selectedFolder?.id?.replace(/^dymax-|^e360-|^cport-/, '') || 'all');
                } else if (selectedFolder?.code === 'all' || selectedFolder?.id?.endsWith('_all')) {
                    folderCode = 'all';
                } else {
                    folderCode = selectedFolder?.code || (selectedFolder?.id?.includes('_') ? selectedFolder.id.split('_')[1] : '01');
                }
                const res = await fetchFiles({
                    projectId: selectedProject.id,
                    folderCode,
                    status: isArchivedProject ? 'archived' : fileStatus,
                    subcat: selectedFolder?.subcat,
                    revisionRound: selectedFolder?.revisionRound,
                });
                if (res?.success && Array.isArray(res.data)) {
                    setFilesList(res.data);
                    setSelectedFile((prev) => (prev && res.data.some((f) => f.id === prev.id) ? prev : (res.data[0] || null)));
                }
                message.success('Files refreshed successfully.');
            } catch (err) {
                message.error('Failed to refresh files.');
            } finally {
                setLoadingFiles(false);
                setIsRefreshing(false);
            }
        } else {
            setIsRefreshing(false);
        }
    };

    // Live search items built from real projects and active files
    const liveSearchItems = useMemo(() => {
        const items = [];
        clientProjects.forEach((p) => {
            items.push({ id: p.id, title: p.name, type: 'Project', icon: 'work' });
        });
        internalWork.forEach((b) => {
            items.push({ id: b.id, title: b.name, type: 'Project', icon: 'domain' });
        });
        return items;
    }, [clientProjects, internalWork]);

    const handleSelectSearchResult = (item) => {
        // Show brief visual loading feedback so the user clearly sees navigation transition
        setLoadingFiles(true);
        setTimeout(() => {
            setLoadingFiles(false);
        }, 300);

        if (item.type === 'File') {
            const itemProjId = item.projectId || (item.project?._id ? item.project._id.toString() : item.project);
            const isInternal = String(itemProjId).startsWith('brand-') || item.folderCode === 'brand-assets' || item.folderCode === 'brand-guidelines';

            if (isInternal) {
                setActiveSection('internal');
                const targetBrand = internalWork.find((b) => b.id === itemProjId) || internalWork[0];
                if (targetBrand) {
                    setSelectedProject(targetBrand);
                    const targetFolder = { id: `${targetBrand.id}_all`, code: 'all', name: 'All Files' };
                    setSelectedFolder(targetFolder);
                    lastInternalSelectionRef.current = { project: targetBrand, folder: targetFolder };
                }
            } else {
                setActiveSection('client');
                const allProj = [...clientProjects, ...archivedProjects];
                const targetProj = allProj.find((p) => p.id === String(itemProjId)) || clientProjects[0];
                if (targetProj) {
                    setSelectedProject(targetProj);
                    const matchingFolder = targetProj.subfolders?.find((f) => f.code === item.folderCode) || targetProj.subfolders?.[0];
                    setSelectedFolder(matchingFolder);
                    lastClientSelectionRef.current = { project: targetProj, folder: matchingFolder };
                }
            }
            setSelectedFile(item);
            message.success(`Navigated to file: ${item.name || item.title}`);
            return;
        }

        if (item.type === 'Project') {
            const isInternal = String(item.id).startsWith('brand-');
            if (isInternal) {
                setActiveSection('internal');
                const targetBrand = internalWork.find((b) => b.id === item.id) || internalWork[0];
                if (targetBrand) {
                    setSelectedProject(targetBrand);
                    const targetFolder = { id: `${targetBrand.id}_all`, code: 'all', name: 'All Files' };
                    setSelectedFolder(targetFolder);
                    lastInternalSelectionRef.current = { project: targetBrand, folder: targetFolder };
                    message.success(`Navigated to brand workspace: ${targetBrand.name}`);
                }
            } else {
                setActiveSection('client');
                const allProj = [...clientProjects, ...archivedProjects];
                const proj = allProj.find((p) => p.name?.toLowerCase() === item.title?.toLowerCase() || p.id === item.id);
                if (proj) {
                    setSelectedProject(proj);
                    setSelectedFolder(proj.subfolders?.[0]);
                    lastClientSelectionRef.current = { project: proj, folder: proj.subfolders?.[0] };
                    message.success(`Navigated to project: ${proj.name}`);
                }
            }
        }
    };

    // Instant section switching preserving last active project and folder
    const handleSectionChange = (sec) => {
        if (sec === activeSection) return;

        // 1. Remember selection for currently active section
        if (activeSection === 'internal') {
            lastInternalSelectionRef.current = { project: selectedProject, folder: selectedFolder };
        } else {
            lastClientSelectionRef.current = { project: selectedProject, folder: selectedFolder };
        }

        setActiveSection(sec);

        // 2. Restore previous selection for new section (or set defaults)
        if (sec === 'internal') {
            const target = lastInternalSelectionRef.current.project
                ? lastInternalSelectionRef.current
                : (internalWork.length > 0 ? { project: internalWork[0], folder: { id: `${internalWork[0].id}_all`, code: 'all', name: 'All Files' } } : null);
            if (target) {
                setSelectedProject(target.project);
                setSelectedFolder(target.folder);
            } else {
                setSelectedProject(null);
                setSelectedFolder(null);
                setFilesList([]);
                setSelectedFile(null);
            }
        } else {
            const target = lastClientSelectionRef.current.project
                ? lastClientSelectionRef.current
                : (clientProjects.length > 0 ? { project: clientProjects[0], folder: clientProjects[0].subfolders?.[0] } : null);
            if (target) {
                setSelectedProject(target.project);
                setSelectedFolder(target.folder);
            } else {
                setSelectedProject(null);
                setSelectedFolder(null);
                setFilesList([]);
                setSelectedFile(null);
            }
        }
    };

    return (
        <div className="files-module h-full max-h-full w-full bg-[#f8f9ff] dark:bg-[#0e1520] rounded-2xl border border-[#c1c6d7] dark:border-[#344258] overflow-hidden flex flex-col md:flex-row shadow-sm">
            {/* Left Column: Real Project & Standard Subfolder Tree Navigation */}
            <div data-tour="files-sidebar-tree" className="h-full flex shrink-0">
                <FilesSidebarTree
                    internalWork={internalWork}
                    clientProjects={clientProjects}
                    archivedProjects={archivedProjects}
                    activeSection={activeSection}
                    loading={scopedProjectId ? false : treeLoading}
                    onSectionChange={handleSectionChange}
                    selectedProjectId={selectedProject?.id}
                    selectedFolderId={selectedFolder?.id}
                    onSelectFolder={handleSelectFolder}
                    scopedProject={scopedProjectId ? (selectedProject || defaultScopedProject) : null}
                />
            </div>

            {/* Right Column: Files Explorer, Details Pane, & Revision History */}
            <div className="flex-1 h-full flex flex-col min-w-0 bg-white dark:bg-[#141c2b] overflow-y-auto files-custom-scrollbar">
                {/* Upper Section: Files Explorer & Grid/List */}
                <section className="p-6 border-b border-[#c1c6d7] dark:border-[#344258] bg-white dark:bg-[#141c2b]">
                    <FilesExplorerHeader
                        projectName={selectedProject?.name || (treeLoading ? 'Loading...' : 'No Project Selected')}
                        folderName={selectedFolder?.name || selectedFolder?.displayName || 'Folder'}
                        fileCount={filesList.length}
                        viewMode={viewMode}
                        onViewModeChange={setViewMode}
                        onOpenSearch={() => setIsSearchOpen(true)}
                        onUploadClick={() => setIsUploadOpen(true)}
                        onRefresh={handleManualRefresh}
                        isRefreshing={isRefreshing}
                        fileStatus={fileStatus}
                        onFileStatusChange={setFileStatus}
                        isArchivedProject={isArchivedProject}
                        onNavigate={onNavigate}
                        hideSearch={Boolean(scopedProjectId)}
                    />

                    <div data-tour="files-grid-list">
                        {loadingFiles ? (
                            <div className="py-16 text-center text-xs text-[#727786]">
                                <span className="material-symbols-outlined text-3xl text-[#0070F3] animate-spin mb-2 block">
                                    sync
                                </span>
                                Loading project files...
                            </div>
                        ) : !selectedProject ? (
                            <div className="py-16 text-center text-xs text-[#727786]">
                                <span className="material-symbols-outlined text-4xl text-[#727786] mb-2 block">
                                    folder_off
                                </span>
                                Select a project from the left navigation to view its files.
                            </div>
                        ) : viewMode === 'grid' ? (
                            <FilesGridView
                                files={filesList}
                                selectedFile={selectedFile}
                                onSelectFile={handleSelectFile}
                                onPreviewFile={handlePreviewFullScreen}
                                onDownloadFile={handleDownload}
                                onPublishToCRE={handlePublishToCRE}
                            />
                        ) : (
                            <FilesListView
                                files={filesList}
                                selectedFile={selectedFile}
                                onSelectFile={handleSelectFile}
                                onPreviewFile={handlePreviewFullScreen}
                                onDownloadFile={handleDownload}
                                onArchiveFile={handleArchiveFile}
                                onRestoreFile={handleRestoreFile}
                                onPublishToCRE={handlePublishToCRE}
                            />
                        )}
                    </div>
                </section>

                {/* Lower Section: Real File Metadata & Preview Panel (75% / 25%) */}
                {selectedFile && (
                    <FilePreviewSection
                        file={selectedFile}
                        onPreviewFullScreen={handlePreviewFullScreen}
                        onDownload={handleDownload}
                        onPublishToCRE={handlePublishToCRE}
                    />
                )}

                {/* Bottom Section: Real Revision History Table */}
                {selectedFile && selectedFile.revisions && selectedFile.revisions.length > 0 && (
                    <FileRevisionHistory
                        file={selectedFile}
                        onViewRevision={(rev) => {
                            message.info(`Viewing revision ${rev.version} (${rev.uploadDate})`);
                        }}
                        onDownloadRevision={(rev) => {
                            handleDownload(selectedFile);
                        }}
                    />
                )}
            </div>

            {/* Spotlight Command-Palette Search Modal (Searching Live Data) */}
            <FileSearchModal
                isOpen={isSearchOpen}
                onClose={() => setIsSearchOpen(false)}
                recentSearches={liveSearchItems}
                onSelectSearchResult={handleSelectSearchResult}
            />

            {/* Real Upload Modal (Cloudflare R2 Multipart Streaming) */}
            <FileUploadModal
                isOpen={isUploadOpen}
                onClose={() => setIsUploadOpen(false)}
                currentProject={selectedProject}
                currentFolder={selectedFolder}
                onUploadSuccess={handleUploadSubmit}
            />

            {/* Comprehensive Full Screen Adaptive File Preview Modal */}
            <FilePreviewModal
                isOpen={previewModal.isOpen}
                file={previewModal.file}
                previewUrl={previewModal.previewUrl}
                onClose={() => setPreviewModal({ isOpen: false, file: null, previewUrl: null })}
                onDownload={handleDownload}
                onArchive={handleArchiveFile}
                onRestore={handleRestoreFile}
            />
        </div>
    );
}
